import { authHeaders, Axios } from '@/utils/axios';
import { API_END_POINTS } from '@/utils/routes';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface ContactPerson {
  name: string;
  email: string;
  phone: string;
}

interface Location {
  address: string;
  city: string;
  country: string;
}

interface PreviousProject {
  projectName: string;
  description: string;
  budget: number;
  completedOn: string;
  _id: string;
}

interface EnterpriseProfile {
  postedJobs: any[];
  EnterpriseId: string;
  companyName: string;
  logo: string;
  industryType: string;
  contactPerson: ContactPerson;
  location: Location;
  preferredCurrency: string;
  preferredPaymentMethod: string;
  vatOrGstId: string;
  socialMediaProfile: string;
  rating: number;
  previousProjects: PreviousProject[];
  _id: string;
  createdAt: string;
  updatedAt: string;
  reviews: any[];
}

interface EnterpriseState {
  profile: EnterpriseProfile | null;
  isLoading: boolean;
  error: string | null;
  fetchEnterpriseProfile: (id: string, token: string) => Promise<void>;
  updateEnterpriseProfile: (id: string, data: Partial<EnterpriseProfile>, token: string) => Promise<void>;
  uploadEnterpriseProfilePicture: (
    file: File,
    token: string,
    userId: string,
    uploadType?: string ,
  ) => Promise<any>;
  clearEnterpriseProfile: () => void;
}

export const useEnterpriseStore = create<EnterpriseState>()(
  persist(
    (set) => ({
      profile: null,
      isLoading: false,
      error: null,

      fetchEnterpriseProfile: async (id, token) => {
        set({ isLoading: true, error: null });
        try {
          const res = await Axios.get(`${API_END_POINTS.GET_ENTERPRISE_PROFILE}/${id}`, authHeaders(token));
          set({ profile: res.data, isLoading: false });
        } catch (err: any) {
          set({ error: err.message || 'Failed to fetch enterprise profile', isLoading: false });
        }
      },

      updateEnterpriseProfile: async (id, updatedData, token) => {
        set({ isLoading: true, error: null });
        try {
          const res = await Axios.put(`${API_END_POINTS.UPDATE_ENTERPRISE_PROFILE}/${id}`, updatedData, authHeaders(token));
          set({ profile: res.data, isLoading: false });
        } catch (err: any) {
          set({ error: err.message || 'Failed to update enterprise profile', isLoading: false });
          throw err;
        }
      },
      clearEnterpriseProfile: () => set({ profile: null, isLoading: false, error: null }),
      uploadEnterpriseProfilePicture: async (
        file: File,
        token: string,
        userId: string,
        uploadType: string = 'enterprise-profile',
      ): Promise<any> => {
        set({ isLoading: true, error: null });

        const formData = new FormData();
        formData.append('profilePicture', file);
        formData.append('userId', userId);
        formData.append('uploadType', uploadType);

        let url = `${API_END_POINTS.UPLOAD_ENTERPRISE_PROFILE_PICTURE}?userId=${encodeURIComponent(userId)}&uploadType=${encodeURIComponent(uploadType)}&role=enterprise`;

        try {
          const res = await Axios.post(
            url,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          set((state) => ({
            profile: {
              ...state.profile,
              profilePicture: res.data.profilePicture,
            },
            isLoading: false,
          }));
          return res;
        } catch (err: any) {
          set({ error: err.message, isLoading: false });
          throw err;
        }
      }

    }),
    {
      name: 'enterprise-storage',
    }
  )
);